import { Fragment, useRef, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { PlusIcon } from "@heroicons/react/24/outline";
import { apiFetch } from "../api/client";
import { useNotification } from "../components/Notifications";
import { mapApiErrorToNotification } from "../utils/notificationHelpers";
import { formatNairaInput } from "../utils/currencyInput";

export default function AddReturnedProduct({ addReturnedProductModalSetting, handlePageUpdate, authContext }) {
    const [open, setOpen] = useState(true);
    const [saleItems, setSaleItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [form, setForm] = useState({ productId: "", quantity: "", refundAmount: "", saleId: "", selectedOption: "", reason: "", customerName: "", returnedAt: new Date().toISOString() });
    const cancelButtonRef = useRef(null);
    const notify = useNotification();

    useEffect(() => {
        fetchSaleItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function fetchSaleItems() {
        setLoading(true);
        try {
            const res = await apiFetch('/api/sale');
            if (!res.ok) {
                // fallback: try products
                await fetchProductsFallback();
                return;
            }
            const data = await res.json().catch(() => null);
            const arr = Array.isArray(data) ? data : (data?.data || data?.Data || []);

            const items = [];
            (arr || []).forEach((sale) => {
                const saleId = sale.saleId || sale.SaleId || sale.SaleId || null;
                const its = sale.items || sale.Items || [];
                (its || []).forEach((it) => {
                    const pid = it.productId || it.ProductId || it.ProductId || it.productId;
                    const pname = it.productName || it.ProductName || it.productName || it.productName || (it.Product && it.Product.name) || pid;
                    items.push({ key: `${saleId}::${pid}`, saleId, productId: pid, productName: pname, saleDate: sale.saleDate || sale.SaleDate || null });
                });
            });

            if (items.length === 0) {
                await fetchProductsFallback();
                return;
            }

            // dedupe by productId so the select shows each product once (only product name requested)
            const map = new Map();
            for (const it of items) {
                if (!it.productId) continue;
                if (!map.has(it.productId)) map.set(it.productId, it);
            }
            const unique = Array.from(map.values());
            setSaleItems(unique);
        } catch (err) {
            console.error('fetchSaleItems error', err);
            await fetchProductsFallback();
        } finally {
            setLoading(false);
        }
    }

    async function fetchProductsFallback() {
        try {
            const res = await apiFetch('/api/product');
            if (!res.ok) return;
            const data = await res.json().catch(() => null);
            const arr = Array.isArray(data) ? data : (data?.data || data?.Data || []);
            const fallback = (arr || []).map((p) => ({ key: `::${p.productId || p.ProductId || p.id}`, saleId: '', productId: p.productId || p.ProductId || p.id, productName: p.name || p.Name || p.productName || p.ProductName || '' }));
            setSaleItems(fallback);
        } catch (err) {
            console.error('fetchProductsFallback error', err);
        }
    }

    const handleChange = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const submit = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);

        // basic validation
        if (!form.productId || !form.quantity || !form.refundAmount) {
            notify('error', { title: 'Validation', text: 'Product, quantity and refund amount are required.' });
            setIsSubmitting(false);
            return;
        }

        const parseNumber = (v) => {
            if (v === null || v === undefined) return 0;
            const s = String(v).replace(/[^0-9.-]+/g, '');
            const n = Number(s);
            return Number.isNaN(n) ? 0 : n;
        };

        const payload = {
            ProductId: form.productId,
            Quantity: parseInt(form.quantity, 10) || 0,
            RefundAmount: parseNumber(form.refundAmount),
            SaleId: form.saleId || null,
            Reason: form.reason || null,
            CustomerName: form.customerName || null,
            ReturnedAt: form.returnedAt ? new Date(form.returnedAt) : null,
        };

        try {
            const res = await apiFetch('/api/returnedProducts/add', { method: 'POST', body: JSON.stringify(payload) });
            if (!res.ok) {
                const parsed = await res.json().catch(() => null);
                const note = mapApiErrorToNotification(parsed, res.status);
                notify('error', note);
                return;
            }
            notify('success', { title: 'Returned', text: 'Returned product recorded.' });
            handlePageUpdate();
            addReturnedProductModalSetting();
        } catch (err) {
            console.error(err);
            notify('error', { title: 'Network error', text: 'Could not reach server.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-10" initialFocus={cancelButtonRef} onClose={setOpen}>
                <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                                            <PlusIcon className="h-6 w-6 text-blue-400" aria-hidden="true" />
                                        </div>
                                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                            <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900 py-2">
                                                Add Returned Product
                                            </Dialog.Title>
                                            <form>
                                                <div className="grid gap-4 mb-4 sm:grid-cols-2">
                                                    <div>
                                                        <label htmlFor="saleItem" className="block mb-2 text-sm font-medium text-gray-900">Product (sold)</label>
                                                        <select id="saleItem" name="saleItem" value={form.selectedOption} onChange={(e) => {
                                                            const val = e.target.value;
                                                            if (!val) {
                                                                setForm({ ...form, productId: '', saleId: '', selectedOption: '' });
                                                                return;
                                                            }
                                                            try {
                                                                const parsed = JSON.parse(val);
                                                                setForm({ ...form, productId: parsed.productId || '', saleId: parsed.saleId || '', selectedOption: val });
                                                            } catch (err) {
                                                                // fallback for legacy values
                                                                const [sid, pid] = val.split('::');
                                                                setForm({ ...form, productId: pid || '', saleId: sid || '', selectedOption: val });
                                                            }
                                                        }} className="w-full border border-gray-300 rounded-lg p-2.5 disabled:opacity-50 disabled:cursor-not-allowed" disabled={isSubmitting}>
                                                            <option value="">Select sold product</option>
                                                            {loading ? <option>Loading...</option> : saleItems.map((s) => (
                                                                <option key={s.key} value={JSON.stringify({ saleId: s.saleId, productId: s.productId })}>{s.productName}</option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    <div>
                                                        <label htmlFor="quantity" className="block mb-2 text-sm font-medium text-gray-900">Quantity</label>
                                                        <input type="number" id="quantity" name="quantity" value={form.quantity} onChange={(e) => handleChange('quantity', e.target.value)} className="w-full border border-gray-300 rounded-lg p-2.5 disabled:opacity-50 disabled:cursor-not-allowed" disabled={isSubmitting} />
                                                    </div>

                                                    <div>
                                                        <label htmlFor="refundAmount" className="block mb-2 text-sm font-medium text-gray-900">Refund Amount</label>
                                                        <input type="text" inputMode="decimal" id="refundAmount" name="refundAmount" value={form.refundAmount} onChange={(e) => handleChange('refundAmount', formatNairaInput(e.target.value))} placeholder={"₦0.00"} className="w-full border border-gray-300 rounded-lg p-2.5 disabled:opacity-50 disabled:cursor-not-allowed" disabled={isSubmitting} />
                                                    </div>

                                                    <div className="sm:col-span-2">
                                                        <label htmlFor="reason" className="block mb-2 text-sm font-medium text-gray-900">Reason for Return</label>
                                                        <textarea id="reason" name="reason" rows="3" value={form.reason} onChange={(e) => handleChange('reason', e.target.value)} className="w-full border border-gray-300 rounded-lg p-2.5 resize-none disabled:opacity-50 disabled:cursor-not-allowed" placeholder="Enter reason" disabled={isSubmitting}></textarea>
                                                    </div>

                                                    <div>
                                                        <label htmlFor="returnedAt" className="block mb-2 text-sm font-medium text-gray-900">Returned Date</label>
                                                        <input type="datetime-local" id="returnedAt" name="returnedAt" value={form.returnedAt?.slice(0, 19)} onChange={(e) => handleChange('returnedAt', e.target.value)} className="w-full border border-gray-300 rounded-lg p-2.5 disabled:opacity-50 disabled:cursor-not-allowed" disabled={isSubmitting} />
                                                    </div>

                                                    <div>
                                                        <label htmlFor="customerName" className="block mb-2 text-sm font-medium text-gray-900">Customer Name</label>
                                                        <input type="text" id="customerName" name="customerName" value={form.customerName} onChange={(e) => handleChange('customerName', e.target.value)} className="w-full border border-gray-300 rounded-lg p-2.5 disabled:opacity-50 disabled:cursor-not-allowed" placeholder="Enter customer name" disabled={isSubmitting} />
                                                    </div>
                                                </div>
                                            </form>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                    <button type="button" className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed sm:ml-3 sm:w-auto" onClick={submit} disabled={isSubmitting} aria-busy={isSubmitting}>{isSubmitting && (<svg className="animate-spin -ml-2 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>)}{isSubmitting ? 'Adding...' : 'Add Returned Product'}</button>
                                    <button type="button" className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto" onClick={() => addReturnedProductModalSetting()} ref={cancelButtonRef}>Cancel</button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
